<script lang="ts" module>
  import {
    cn,
    tableVariants,
    uniqueContext,
    useActions,
    type HTMLActionArray,
  } from "#lib"
  import type { Writable } from "svelte/store"

  export type RootContext = {
    cols: number
    variant: VariantProps<typeof tableVariants>["variant"]
    responsive: boolean
    headerLabels: (string | null)[]
  }

  const { getContext, setContext } = uniqueContext<Writable<RootContext>>()
  export const getTableRootContext = getContext
</script>

<script lang="ts">
  import { useMutationObserver } from "#lib/internal/actions"
  import type { VariantProps } from "class-variance-authority"
  import type { Snippet } from "svelte"
  import { writable } from "svelte/store"

  type Props = {
    cols?: number
    variant?: VariantProps<typeof tableVariants>["variant"]
    responsive?: boolean
    use?: HTMLActionArray
    tableWidth?: number
    class?: string
    children?: Snippet
    [key: string]: unknown
  }

  let {
    cols = 12,
    variant = "default",
    responsive = false,
    use = [],
    tableWidth = $bindable(0),
    class: cssClass = "",
    children,
    ...restProps
  }: Props = $props()

  let headerLabels: string[] = []
  let rowCount: number | undefined = $state(undefined)

  const rootCtx = writable<RootContext>({
    cols,
    variant,
    responsive,
    headerLabels,
  })

  setContext(rootCtx)

  $effect(() => {
    rootCtx.update((state) => ({
      ...state,
      cols,
      variant,
      responsive,
      headerLabels,
    }))
  })

  const mutationObserverOptions = {
    callback: (mutationList: MutationRecord[]) => {
      const table = mutationList[0].target as HTMLElement
      rowCount = table.querySelectorAll("[role='row']").length
    },
    childList: true,
  }
</script>

<div
  role="table"
  class={cn(tableVariants({ variant }), cssClass)}
  aria-rowcount={rowCount}
  use:useActions={[...use, [useMutationObserver, mutationObserverOptions]]}
  bind:clientWidth={tableWidth}
  {...restProps}
>
  {@render children?.()}
</div>
